// src/features/caas/hooks/useCaasTeam.ts
import { useState, useEffect, useCallback } from 'react';
import { CaasTeam, FabrikUser } from '../../../types';
import { getTeam, listTeamMembers } from '../../../services/api/endpoints/caas';
import { useAuth } from '../../../contexts/AuthContext';

interface UseCaasTeamResult {
  team: CaasTeam | null;
  members: FabrikUser[];
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

export const useCaasTeam = (teamId: string | undefined): UseCaasTeamResult => {
  const { user } = useAuth();
  const [team, setTeam] = useState<CaasTeam | null>(null);
  const [members, setMembers] = useState<FabrikUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTeamData = useCallback(async () => {
    if (!teamId) {
      setError('No team ID provided');
      setIsLoading(false);
      return;
    }

    if (!user?.token) {
      setError('No authentication token available');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Fetch team details
      const teamData = await getTeam(user.token, teamId);
      setTeam(teamData);

      // Fetch team members
      const teamMembers = await listTeamMembers(user.token, teamId);
      setMembers(teamMembers);
    } catch (err: any) {
      console.error('Error fetching CaaS team:', err);
      setError(err.message || 'Failed to load CaaS team');
    } finally {
      setIsLoading(false);
    }
  }, [teamId, user?.token]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  return {
    team,
    members,
    isLoading,
    error,
    refresh: fetchTeamData,
  };
};
